import {
  Alert,
  Button,
  Checkbox,
  FilterOptionsState,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { AutocompleteOption, ComboField } from 'components/shared/ComboField';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { AuthStore } from 'stores/users/AuthStore';
import { UserProfileStore } from 'stores/users/UserProfileStore';
import { featureFlags } from 'utils/config';
import { userSettings } from 'utils/labels';
import { jobRoleOptions } from 'utils/options';

const noFilter = createFilterOptions<AutocompleteOption>();
const limitFilter = createFilterOptions<AutocompleteOption>({ limit: 20 });
const lengthAndLimitFilter = (
  options: AutocompleteOption[],
  state: FilterOptionsState<AutocompleteOption>
): AutocompleteOption[] => {
  return state.inputValue.length >= 4 ? limitFilter(options, state) : [];
};

type UserProfileProps = {
  userProfileStore: UserProfileStore;
  isInternal: boolean;
  authStore: AuthStore;
};

const UserProfile = observer(({ userProfileStore, isInternal, authStore }: UserProfileProps) => {
  const { getFeatureFlagValueForUser } = authStore;
  const { form, message, status, changeUserProfile, accounts } = userProfileStore;
  if (!accounts) return null;

  const hasCostarLicenseField = form.$('hasCostarLicense');
  const hasCostarLicense = hasCostarLicenseField.value;

  const showNewClientTech = getFeatureFlagValueForUser(featureFlags.showNewClientTech);

  const handleCostarLicenseChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const value = evt.target.checked;
      hasCostarLicenseField.set(value);
    },
    [hasCostarLicenseField]
  );

  const handleKeyInField = (evt: KeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.stopPropagation();
      evt.preventDefault();
      if (form.isValid) {
        changeUserProfile();
      }
    }
  };

  return (
    <div aria-label="user profile page" style={{ width: 400, marginTop: 60, marginBottom: 30 }}>
      <form autoComplete="off">
        <Stack spacing={4}>
          <Typography variant="h5">
            <strong>Profile Information</strong>
          </Typography>
          {message && (
            <Alert aria-label="form-error" severity={status === 'error' ? 'error' : 'success'}>
              {message}
            </Alert>
          )}
          <TextField
            variant="outlined"
            autoComplete="off"
            onKeyDown={handleKeyInField}
            data-testid="fullName"
            required={!isInternal}
            {...form.$('fullName').bind()}
          />
          <TextField
            variant="outlined"
            autoComplete="off"
            onKeyDown={handleKeyInField}
            data-testid="email"
            required
            {...form.$('email').bind()}
            helperText="After change you will be logged out and need to confirm new email."
          />
          <ComboField
            field={form.$('jobRole')}
            options={jobRoleOptions}
            AutoCompleteProps={{ openOnFocus: true, selectOnFocus: true }}
            TextFieldProps={{
              'data-testid': 'jobRole',
              required: !isInternal,
            }}
            filter={noFilter}
          />
          <ComboField
            field={form.$('companyName')}
            options={accounts}
            TextFieldProps={{
              'data-testid': 'companyName',
              required: !isInternal,
            }}
            filter={lengthAndLimitFilter}
          />
          {showNewClientTech && (
            <FormControlLabel
              control={
                <Checkbox
                  data-testid="hasCostarLicense"
                  checked={hasCostarLicense}
                  onChange={handleCostarLicenseChange}
                />
              }
              label={userSettings.hasCostarLicense}
            />
          )}
          <Button
            variant="contained"
            disableElevation
            style={{ height: 56 }}
            disabled={!form.isValid}
            onClick={() => changeUserProfile()}
          >
            Save Changes
          </Button>
        </Stack>
      </form>
    </div>
  );
});

export { UserProfile };
